import { format } from 'date-fns';
import Link from 'next/link';
import { memo, type ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';

import { useTrackDepletedStateViewed, useTrackDepletedUpsellClicked } from '@air/analytics';
import { Button } from '@air/primitive-button';
import { Modal, ModalCloseButton, ModalTitle } from '@air/primitive-modal';
import { useAirModal } from '@air/provider-modal';
import { parseAirDateToISO } from '@air/utilities';

import { EnterprisePlanModal } from '~/components/Modals/EnterprisePlanModal';
import { PlansModal } from '~/components/Modals/PlansModal/PlansModal';
import { Routes } from '~/constants/routes';
import { useCreditAnalyticsParams } from '~/hooks/useCreditAnalyticsParams';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';
import { useCreditStats } from '~/swr-hooks/workspaces/useCreditStats';
import { canManageSubscription } from '~/utils/permissions/workspacePermissions';

export type CreditsDepletedModalProps = {
  triggeredBy?: 'canvas';
};

export const CreditsDepletedModal = memo(({ triggeredBy, onClose }: AirModalProps<CreditsDepletedModalProps>) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const { data: creditStats } = useCreditStats();
  const { data: subscription } = useSubscriptionContext();
  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const canEditPlan = canManageSubscription(permissions);

  const [showPlansModal] = useAirModal(PlansModal);
  const [showEnterprisePlanModal] = useAirModal(EnterprisePlanModal);

  const { trackDepletedStateViewed } = useTrackDepletedStateViewed();
  const { trackDepletedUpsellClicked } = useTrackDepletedUpsellClicked();
  const creditAnalyticsParams = useCreditAnalyticsParams();
  const hasTrackedView = useRef(false);

  const surface = triggeredBy === 'canvas' ? 'canvas' : 'upload_modal';

  // useEffect re-runs when creditAnalyticsParams loads; guard prevents duplicate events
  useEffect(() => {
    if (creditAnalyticsParams && !hasTrackedView.current) {
      hasTrackedView.current = true;
      trackDepletedStateViewed({
        surface,
        days_since_cycle_start: creditAnalyticsParams.days_since_cycle_start,
      });
    }
  }, [creditAnalyticsParams, surface, trackDepletedStateViewed]);

  const handleClick = useCallback(() => {
    // Only track upsell click when user can actually upgrade (not viewing member list)
    if (canEditPlan && creditAnalyticsParams) {
      trackDepletedUpsellClicked({
        surface,
        days_since_cycle_start: creditAnalyticsParams.days_since_cycle_start,
      });
    }

    if (canEditPlan) {
      if (subscription?.isEnterprise) {
        showEnterprisePlanModal();
      } else {
        showPlansModal();
      }
    }

    onClose();
  }, [
    canEditPlan,
    creditAnalyticsParams,
    onClose,
    showEnterprisePlanModal,
    showPlansModal,
    subscription?.isEnterprise,
    surface,
    trackDepletedUpsellClicked,
  ]);

  const { title, description } = useMemo(() => {
    let title = 'You’re out of credits';
    let actionText = 'Uploads are';
    const refreshDateText = creditStats?.nextCycleStartAt
      ? `refresh on ${format(parseAirDateToISO(creditStats.nextCycleStartAt), 'MMMM d')}`
      : 'refresh';

    if (triggeredBy === 'canvas') {
      title = 'Your canvas is locked';
      actionText = 'Canvas editing is';
    }

    let description: ReactNode = `${actionText} paused until your credits ${refreshDateText} or you upgrade your plan. Your workspace, search, and shared links are still fully accessible.`;

    if (!canEditPlan && !!currentWorkspace) {
      description = (
        <>
          {description}{' '}
          <span className="font-semibold">Contact the admin for {currentWorkspace.name} to upgrade your plan.</span>
        </>
      );
    }

    return {
      title,
      description,
    };
  }, [canEditPlan, creditStats?.nextCycleStartAt, currentWorkspace, triggeredBy]);

  return (
    <Modal
      isOpen
      onDismiss={onClose}
      className="mt-0 flex w-[354px] flex-col gap-4"
      overlayClassName="flex items-center justify-center"
    >
      <header className="flex justify-between">
        <ModalTitle>{title}</ModalTitle>
        <ModalCloseButton className="shrink-0" onClick={onClose} />
      </header>
      <p className="text-14 text-gray-11">{description}</p>
      <div className="flex justify-end gap-2">
        {canEditPlan ? (
          <Button type="button" onClick={handleClick} size="large" appearance="filled" color="blue" className="w-full">
            {subscription?.isEnterprise ? 'Contact support' : 'Upgrade plan'}
          </Button>
        ) : (
          <>
            <Button type="button" size="large" appearance="outline" color="grey" onClick={onClose}>
              Cancel
            </Button>
            <Button type="button" size="large" appearance="filled" color="blue" asChild>
              <Link href={Routes.workspaceSettings.members} onClick={handleClick}>
                View workspace user list
              </Link>
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
});

CreditsDepletedModal.displayName = 'CreditsDepletedModal';
